<script lang="ts">
import { defineComponent, type PropType } from 'vue';

export default defineComponent({
  name: 'pagination',
  computed: {
    pages() {
      return Math.ceil(this?.count / this.size);
    },
    current() {
      return Math.floor(this?.offset / this.size) + 1;
    },
    isFirstPage() {
      return this.offset < this.size;
    },
    isLastPage() {
      return this.count - this.offset <= this.size;
    },
    pageSize: {
      get() { return this.size; },
      set(v: number) {
        this.$emit('resize', +v);
      }
    },
    sizes() {
      return this.customSizes || [10, 15, 20, 50];
    },
  },
  emits: ['flip', 'resize'],
  methods: {
    start() {
      if (!this.isFirstPage) this.$emit('flip', 0);
    },
    backward() {
      if (!this.isFirstPage) this.$emit('flip', this.offset - this.size);
    },
    forward() {
      if (!this.isLastPage) this.$emit('flip', this.offset + this.size);
    },
    end() {
      if (!this.isLastPage) this.$emit('flip', Math.floor(this.count / this.size) * this.size);
    },
  },
  props: {
    offset: { type: Number, required: true },
    size: { type: Number, required: true },
    count: { type: Number, required: true },
    resizable: { type: Boolean, required: false },
    customSizes: { type: Array as PropType<number[]>, required: false },
    refresh: { type: Function as PropType<(e: MouseEvent) => void>, required: false },
  }
});
</script>

<template>
  <div class="text-center relative">
    <template v-if="count">
      <Button
        @click="start"
        :class="{ button: true, [isFirstPage ? 'disabled' : 'enabled']: true }"
      >
        <i class="pi pi-fast-backward" />
      </Button>
      <Button
        @click="backward"
        :class="{ button: true, [isFirstPage ? 'disabled' : 'enabled']: true }"
      >
        <i class="pi pi-arrow-left" />
      </Button>
      <Button v-if="refresh" @click="refresh" class="button enabled">
        <i class="pi pi-refresh" />
      </Button>
      <div class="inline-block w-[10em]">Страница {{ current }} / {{ pages }}</div>
      <Button
        @click="forward"
        :class="{ button: true, [isLastPage ? 'disabled' : 'enabled']: true }"
      >
        <i class="pi pi-arrow-right" />
      </Button>
      <Button @click="end" :class="{ button: true, [isLastPage ? 'disabled' : 'enabled']: true }">
        <i class="pi pi-fast-forward" />
      </Button>
    </template>
    <template v-else>
      <div>
        <Button class="button disabled"><i class="pi pi-fast-backward" /></Button>
        <Button class="button disabled"><i class="pi pi-arrow-left" /></Button>
        <Button class="button disabled" v-if="refresh"><i class="pi pi-refresh" /></Button>
        <div class="inline-block w-[10em] disabled">Нет данных</div>
        <Button class="button disabled"><i class="pi pi-arrow-right" /></Button>
        <Button class="button disabled"><i class="pi pi-fast-forward" /></Button>
      </div>
    </template>
    <template v-if="resizable">
      <div class="absolute right-0 top-0 bottom-0 place-content-center flex gap-1 p-3">
        <span>Записей на странице:</span>
        <span
          v-for="size in sizes"
          @click="pageSize = size"
          :class="{ 'text-green-500': size === pageSize, 'cursor-pointer': true }"
        > {{ size }} </span>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.button {
  @apply my-1 size-10 border border-transparent cursor-auto rounded-lg;
}

.enabled {
  @apply cursor-pointer;
  @apply text-emerald-700 hover:border-emerald-700;
  @apply dark:text-emerald-500 dark:hover:border-emerald-500;
}

.disabled {
  @apply text-gray-300 dark:text-gray-700;
}
</style>
